import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  ButtonGroup,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Box,
  Collapsible,
  List,
  ProgressBar,
  Divider,
  DataTable,
} from '@shopify/polaris';
import { useShop } from '../context/ShopContext';
import { useOperationProgressContext } from '../context/OperationProgressContext';
import { getSessionToken } from '../hooks/useApi';

interface TestStatus {
  shopDomain: string;
  shopExists: boolean;
  shopInfo: {
    Name: string;
    Email: string;
    ShopIdInt: number;
    hasAccessToken: boolean;
    InstalledAt: string;
    IsActive: boolean;
  } | null;
  productCount: number;
  behaviorCount: number;
  mlModelsCount: number;
  configuredDefaultShop: string;
  hasConfiguredToken: boolean;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

// Get the API base URL - uses relative URL in production (same origin)
const API_BASE = import.meta.env.VITE_API_URL || '';

export default function TestingPage() {
  const navigate = useNavigate();
  const { shop } = useShop();
  const { startOperation } = useOperationProgressContext();
  const [status, setStatus] = useState<TestStatus | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ApiResponse | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [preComputeStatsData, setPreComputeStatsData] = useState<any>(null);
  const [orderSyncStatus, setOrderSyncStatus] = useState<any>(null);

  // Step 7-12 result states
  const [abResult, setAbResult] = useState<any>(null);
  const [abExperimentId, setAbExperimentId] = useState<string | null>(null);
  const [attributionResult, setAttributionResult] = useState<any>(null);
  const [banditResult, setBanditResult] = useState<any>(null);
  const [semanticResult, setSemanticResult] = useState<any>(null);
  const [sessionScoringResult, setSessionScoringResult] = useState<any>(null);
  const [abandonedCartResult, setAbandonedCartResult] = useState<any>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 50));
  };

  // Helper to add shop param to endpoint
  const withShop = (endpoint: string) => {
    if (!shop) return endpoint;
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}shopDomain=${encodeURIComponent(shop)}`;
  };

  const apiCall = async (endpoint: string, method: string = 'GET', body?: any): Promise<ApiResponse> => {
    try {
      // Get session token for authentication
      const sessionToken = await getSessionToken();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      };
      
      // Add Bearer token if available
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const options: RequestInit = {
        method,
        headers,
      };
      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${API_BASE}/api/testing/${endpoint}`, options);
      const data = await response.json();

      if (response.ok) {
        return { success: true, data, message: data.message };
      } else {
        return { success: false, error: data.error || 'Unknown error', data };
      }
    } catch (error) {
      return { success: false, error: String(error) };
    }
  };

  const refreshStatus = useCallback(async () => {
    if (!shop) {
      addLog('⚠️ No shop context available');
      return;
    }
    setLoading('status');
    addLog(`Fetching status for ${shop}...`);
    const result = await apiCall(withShop('status'));
    if (result.success) {
      setStatus(result.data);
      addLog('✅ Status fetched successfully');
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLastResult(result);
    setLoading(null);
  }, [shop]);

  // Auto-refresh status when shop changes
  useEffect(() => {
    if (shop) {
      refreshStatus();
    }
  }, [shop, refreshStatus]);

  const initShop = async () => {
    setLoading('init-shop');
    addLog('Initializing shop...');
    const result = await apiCall(withShop('init-shop'), 'POST', { shopDomain: shop });
    setLastResult(result);
    if (result.success) {
      addLog(`✅ Shop initialized: ${result.data?.shopName || 'OK'}`);
      await refreshStatus();
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const getInstallUrl = async () => {
    setLoading('install-url');
    addLog('Getting OAuth install URL...');
    const result = await apiCall(withShop('install-url'));
    setLastResult(result);
    if (result.success && result.data?.installUrl) {
      addLog(`✅ Install URL generated`);
      // Open in new window
      window.open(result.data.installUrl, '_blank');
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const syncProducts = async () => {
    setLoading('sync-products');
    addLog('Syncing products from Shopify...');
    const result = await apiCall(withShop('sync-products'), 'POST', { shopDomain: shop });
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'SyncProducts', result.data.message);
        addLog(`🚀 Sync started - follow progress in the floating bar`);
      } else {
        addLog(`✅ Synced ${result.data?.uniqueProducts || result.data?.totalProducts || 0} products`);
        await refreshStatus();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const simulateBehaviors = async (count: number = 1000) => {
    setLoading('simulate-behaviors');
    addLog(`Simulating ${count} user behaviors...`);
    const result = await apiCall(withShop('simulate-behaviors'), 'POST', { shopDomain: shop, count });
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'SimulateBehaviors', result.data.message);
        addLog(`🚀 Behavior simulation started - follow progress in the floating bar`);
      } else {
        const breakdown = result.data?.breakdown;
        addLog(`✅ Created ${count} behaviors (${breakdown?.views} views, ${breakdown?.addToCarts} carts, ${breakdown?.purchases} purchases)`);
        await refreshStatus();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const simulateNavigation = async (sessionCount: number = 100) => {
    setLoading('simulate-navigation');
    addLog(`Simulating ${sessionCount} realistic user navigation sessions...`);
    const result = await apiCall(withShop('simulate-navigation'), 'POST', { 
      shopDomain: shop, 
      sessionCount,
      includeRecommendationClicks: true 
    });
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'SimulateNavigation', result.data.message);
        addLog(`🚀 Navigation simulation started - follow progress in the floating bar`);
      } else {
        const breakdown = result.data?.breakdown;
        addLog(`✅ Created ${result.data?.behaviorsCreated} behaviors from ${sessionCount} sessions`);
        addLog(`   📊 Views: ${breakdown?.views}, Rec Clicks: ${breakdown?.recommendationClicks}, Purchases: ${breakdown?.purchases}`);
        await refreshStatus();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const testRecommendations = async () => {
    setLoading('recommendations');
    addLog('Testing recommendations...');
    // First get a real product ID from the database
    const result = await apiCall(withShop('sample-product'));
    if (!result.success || !result.data?.productId) {
      addLog('❌ No products found. Sync products first.');
      setLastResult(result);
      setLoading(null);
      return;
    }
    const productId = result.data.productId;
    addLog(`Using product ID: ${productId}`);
    
    const recResult = await apiCall(withShop(`recommendations/${productId}`));
    setLastResult(recResult);
    if (recResult.success) {
      const count = recResult.data?.recommendations?.length || 0;
      addLog(`✅ Got ${count} recommendations for product ${productId}`);
    } else {
      addLog(`❌ Error: ${recResult.error}`);
    }
    setLoading(null);
  };

  const resetData = async (includeShop: boolean = false) => {
    setLoading('reset');
    addLog(`Resetting data${includeShop ? ' (including shop)' : ''}...`);
    const result = await apiCall(withShop(`reset-data?includeShop=${includeShop}`), 'DELETE');
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'ResetShopData', result.data.message);
        addLog(`🚀 Operation started - follow progress in the floating bar`);
      } else {
        addLog(`✅ Data reset: ${result.data?.deletedProducts} products, ${result.data?.deletedBehaviors} behaviors`);
        await refreshStatus();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const trainModel = async () => {
    setLoading('train-model');
    addLog('Training ML model (Matrix Factorization)...');
    const result = await apiCall(withShop('train-model'), 'POST', { shopDomain: shop });
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'TrainModel', result.data.message);
        addLog(`🚀 Model training started - follow progress in the floating bar`);
      } else {
        const data = result.data;
        addLog(`✅ Model trained in ${data?.trainingTimeSeconds?.toFixed(2)}s`);
        addLog(`   📊 Training data: ${data?.trainingDataCount} behaviors`);
        addLog(`   🎯 Accuracy (R²): ${(data?.model?.accuracy * 100)?.toFixed(1)}%`);
        await refreshStatus();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
      if (result.data?.suggestion) {
        addLog(`   💡 ${result.data.suggestion}`);
      }
    }
    setLoading(null);
  };

  const installWidget = async () => {
    setLoading('install-widget');
    addLog('Installing widget on store...');
    const result = await apiCall(withShop('install-widget'), 'POST', { shopDomain: shop });
    setLastResult(result);
    if (result.success) {
      addLog(`✅ Widget installed!`);
      addLog(`   🔗 Script: ${result.data?.scriptUrl}`);
      addLog(`   🛒 Visit your store products to see recommendations`);
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const uninstallWidget = async () => {
    setLoading('uninstall-widget');
    addLog('Uninstalling widget from store...');
    const result = await apiCall(withShop('uninstall-widget'), 'DELETE');
    setLastResult(result);
    if (result.success) {
      addLog(`✅ Widget uninstalled. Removed ${result.data?.removedCount} script tag(s)`);
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  // Order Sync functions
  const getOrderSyncStatus = async () => {
    setLoading('order-sync-status');
    addLog('Fetching order sync status...');
    try {
      const response = await fetch(`${API_BASE}/api/orders/sync/status?shopDomain=${encodeURIComponent(shop || '')}`, {
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      const data = await response.json();
      if (response.ok) {
        setOrderSyncStatus(data);
        addLog(`✅ Sync status: ${data.totalOrdersSynced} orders synced`);
      } else {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${String(error)}`);
    }
    setLoading(null);
  };

  const syncHistoricalOrders = async (maxOrders: number = 500, monthsBack: number = 12) => {
    setLoading('sync-historical');
    addLog(`Syncing historical orders (max ${maxOrders}, last ${monthsBack} months)...`);
    try {
      const response = await fetch(`${API_BASE}/api/orders/sync/historical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ shopDomain: shop, maxOrders, monthsBack }),
      });
      const data = await response.json();
      setLastResult({ success: response.ok, data });
      if (response.ok) {
        addLog(`✅ Synced ${data.ordersProcessed} orders (${data.ordersSkipped} skipped)`);
        addLog(`   📊 Total order items: ${data.totalOrdersFetched}`);
        await getOrderSyncStatus();
      } else {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${String(error)}`);
    }
    setLoading(null);
  };

  const syncIncrementalOrders = async () => {
    setLoading('sync-incremental');
    addLog('Syncing new orders since last sync...');
    try {
      const response = await fetch(`${API_BASE}/api/orders/sync/incremental`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ shopDomain: shop }),
      });
      const data = await response.json();
      setLastResult({ success: response.ok, data });
      if (response.ok) {
        addLog(`✅ Synced ${data.ordersProcessed} new orders`);
        await getOrderSyncStatus();
      } else {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${String(error)}`);
    }
    setLoading(null);
  };

  const generateRealisticSales = async (totalSales: number = 200, daysBack: number = 90) => {
    setLoading('generate-sales');
    addLog(`Generating ${totalSales} realistic sales over ${daysBack} days...`);
    try {
      const response = await fetch(`${API_BASE}/api/testing/generate-sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ shopDomain: shop, totalSales, daysBack }),
      });
      const data = await response.json();
      setLastResult({ success: response.ok, data });
      if (response.ok) {
        addLog(`✅ Generated ${data.behaviorsCreated} purchase behaviors`);
        addLog(`   🛒 FBT pairs: ${data.fbtPairsCreated}, Products used: ${data.productsUsed}`);
        await refreshStatus();
      } else {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${String(error)}`);
    }
    setLoading(null);
  };

  // Pre-compute functions
  const preComputeAll = async () => {
    setLoading('precompute-all');
    addLog('Running ALL pre-computation tasks...');
    const result = await apiCall(withShop('precompute/all'), 'POST');
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'PrecomputeRecommendations', result.data.message);
        addLog(`🚀 Pre-computation started - follow progress in the floating bar`);
      } else {
        addLog(`✅ Pre-computation complete in ${result.data?.totalDurationMs}ms`);
        result.data?.tasks?.forEach((t: any) => {
          addLog(`   ✓ ${t.task}: ${t.durationMs}ms`);
        });
        // Also refresh stats
        await preComputeStats();
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const preComputeStats = async () => {
    setLoading('precompute-stats');
    addLog('Fetching pre-compute stats...');
    const result = await apiCall(withShop('precompute/stats'));
    setLastResult(result);
    if (result.success) {
      setPreComputeStatsData(result.data);
      addLog(`✅ Stats: ${result.data?.validRecommendations?.total || 0} valid recommendations cached`);
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const preComputeTask = async (task: string) => {
    setLoading(`precompute-${task}`);
    addLog(`Running pre-compute: ${task}...`);
    const result = await apiCall(withShop(`precompute/${task}`), 'POST');
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, `Precompute_${task}`, result.data.message);
        addLog(`🚀 ${task} started - follow progress in the floating bar`);
      } else {
        addLog(`✅ ${task} complete in ${result.data?.durationMs}ms`);
        await preComputeStats(); // Refresh stats after
      }
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  const clearRecommendationCache = async () => {
    setLoading('clear-cache');
    addLog('Clearing recommendation cache...');
    const result = await apiCall(withShop('precompute/clear'), 'DELETE');
    setLastResult(result);
    if (result.success) {
      // If it returns correlationId, the operation runs on Azure Functions
      if (result.data?.correlationId) {
        startOperation(result.data.correlationId, 'BulkDeleteRecommendations', result.data.message);
        addLog(`🚀 Cleanup started - follow progress in the floating bar`);
      } else {
        addLog(`✅ Cleared ${result.data?.deletedRecommendations} recommendations, ${result.data?.deletedEmbeddings} embeddings`);
      }
      setPreComputeStatsData(null);
    } else {
      addLog(`❌ Error: ${result.error}`);
    }
    setLoading(null);
  };

  return (
    <Page
      title="🧪 Testing Dashboard"
      backAction={{ content: 'Back', onAction: () => navigate('/app') }}
      subtitle={`Integration testing tools for NexusAI Recommendations${shop ? ` - ${shop}` : ''}`}
      primaryAction={{
        content: 'Refresh Status',
        onAction: refreshStatus,
        loading: loading === 'status',
        disabled: !shop,
      }}
    >
      <Layout>
        {/* No Shop Warning */}
        {!shop && (
          <Layout.Section>
            <Banner tone="critical">
              <p>No shop context detected. Make sure you're accessing this page with <code>?shop=your-store.myshopify.com</code> parameter.</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Status Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">System Status</Text>
                {!shop ? (
                  <Badge tone="critical">No Shop Context</Badge>
                ) : status ? (
                  <Badge tone={status.shopExists ? 'success' : 'warning'}>
                    {status.shopExists ? 'Shop Connected' : 'No Shop'}
                  </Badge>
                ) : (
                  <Badge>Loading...</Badge>
                )}
              </InlineStack>

              {status ? (
                <BlockStack gap="300">
                  <InlineStack gap="400" wrap>
                    <Box>
                      <Text as="span" tone="subdued">Shop: </Text>
                      <Text as="span" fontWeight="semibold">{status.shopDomain}</Text>
                    </Box>
                    <Box>
                      <Text as="span" tone="subdued">Products: </Text>
                      <Text as="span" fontWeight="semibold">{status.productCount}</Text>
                    </Box>
                    <Box>
                      <Text as="span" tone="subdued">Behaviors: </Text>
                      <Text as="span" fontWeight="semibold">{status.behaviorCount}</Text>
                    </Box>
                    <Box>
                      <Text as="span" tone="subdued">ML Models: </Text>
                      <Text as="span" fontWeight="semibold">{status.mlModelsCount}</Text>
                    </Box>
                  </InlineStack>

                  {status.shopInfo && (
                    <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                      <Text as="p">
                        <strong>{status.shopInfo.Name}</strong> ({status.shopInfo.Email})
                        <br />
                        Shop ID: {status.shopInfo.ShopIdInt} | 
                        Active: {status.shopInfo.IsActive ? 'Yes' : 'No'} |
                        Token: {status.shopInfo.hasAccessToken ? '✅' : '❌'}
                      </Text>
                    </Box>
                  )}
                </BlockStack>
              ) : (
                <Banner tone="info">
                  <p>Click "Refresh Status" to load system information</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 1: Initialize Shop */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 1: Initialize Shop</Text>
              <Text as="p" tone="subdued">
                Connect the dev store to the system. This requires an access token from OAuth flow.
              </Text>
              <ButtonGroup>
                <Button
                  onClick={getInstallUrl}
                  loading={loading === 'install-url'}
                  variant="primary"
                >
                  🔐 Start OAuth Flow
                </Button>
                <Button
                  onClick={initShop}
                  loading={loading === 'init-shop'}
                  disabled={!status?.hasConfiguredToken}
                >
                  Initialize Shop (with configured token)
                </Button>
              </ButtonGroup>
              {!status?.shopExists && (
                <Banner tone="warning">
                  <p>No shop connected. Click "Start OAuth Flow" to authorize the app.</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 2: Sync Products */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 2: Sync Products</Text>
              <Text as="p" tone="subdued">
                Sync your product catalog from Shopify to the local database.
              </Text>
              <Button
                onClick={syncProducts}
                loading={loading === 'sync-products'}
                disabled={!status?.shopExists}
                variant="primary"
              >
                📦 Sync Products from Shopify
              </Button>
              {status && status.productCount > 0 && (
                <Banner tone="success">
                  <p>{status.productCount} products synced</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 2.5: Sync Historical Orders */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">Step 2.5: Sync Historical Orders</Text>
                <Button
                  onClick={getOrderSyncStatus}
                  loading={loading === 'order-sync-status'}
                  disabled={!status?.shopExists}
                  size="slim"
                >
                  Refresh Status
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                Import real purchase history from Shopify to train the Frequently Bought Together (FBT) recommendations.
                This is automatically triggered when the app is installed.
              </Text>

              <InlineStack gap="300">
                <Button
                  onClick={() => syncHistoricalOrders(100, 6)}
                  loading={loading === 'sync-historical'}
                  disabled={!status?.shopExists}
                >
                  Sync 100 Orders (6 months)
                </Button>
                <Button
                  onClick={() => syncHistoricalOrders(500, 12)}
                  loading={loading === 'sync-historical'}
                  disabled={!status?.shopExists}
                  variant="primary"
                >
                  📥 Sync 500 Orders (12 months)
                </Button>
                <Button
                  onClick={syncIncrementalOrders}
                  loading={loading === 'sync-incremental'}
                  disabled={!status?.shopExists}
                >
                  Sync New Orders Only
                </Button>
              </InlineStack>

              {orderSyncStatus && (
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="200">
                    <Text as="h4" fontWeight="semibold">📊 Order Sync Status</Text>
                    <InlineStack gap="400" wrap>
                      <Box>
                        <Text as="span" tone="subdued">Total Synced: </Text>
                        <Text as="span" fontWeight="bold">{orderSyncStatus.totalOrdersSynced || 0}</Text>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">Status: </Text>
                        <Badge tone={orderSyncStatus.orderSyncStatus === 'completed' ? 'success' : 'info'}>
                          {orderSyncStatus.orderSyncStatus || 'Not started'}
                        </Badge>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">Initial Sync: </Text>
                        <Text as="span" fontWeight="bold">{orderSyncStatus.initialSyncComplete ? '✅ Complete' : '⏳ Pending'}</Text>
                      </Box>
                    </InlineStack>
                    {orderSyncStatus.ordersSyncedAt && (
                      <Text as="p" tone="subdued" variant="bodySm">
                        Last synced: {new Date(orderSyncStatus.ordersSyncedAt).toLocaleString()}
                      </Text>
                    )}
                  </BlockStack>
                </Box>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 3: Simulate Behaviors */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 3: Simulate User Behaviors</Text>
              <Text as="p" tone="subdued">
                Generate simulated user behavior data for testing the recommendation engine.
              </Text>
              <InlineStack gap="300" blockAlign="center">
                <ButtonGroup>
                  <Button
                    onClick={() => simulateBehaviors(100)}
                    loading={loading === 'simulate-behaviors'}
                    disabled={!status?.shopExists || status?.productCount === 0}
                  >
                    Generate 100 Behaviors
                  </Button>
                  <Button
                    onClick={() => simulateBehaviors(1000)}
                    loading={loading === 'simulate-behaviors'}
                    disabled={!status?.shopExists || status?.productCount === 0}
                    variant="primary"
                  >
                    Generate 1,000 Behaviors
                  </Button>
                  <Button
                    onClick={() => simulateBehaviors(5000)}
                    loading={loading === 'simulate-behaviors'}
                    disabled={!status?.shopExists || status?.productCount === 0}
                  >
                    Generate 5,000 Behaviors
                  </Button>
                </ButtonGroup>
              </InlineStack>
              
              <Text as="p" fontWeight="semibold">🎯 Ultra-Realistic Navigation Simulation:</Text>
              <Text as="p" tone="subdued">
                Simulates real user navigation patterns including browsing categories, 
                clicking recommendations, returning customers, and realistic conversion funnels.
              </Text>
              <InlineStack gap="300">
                <Button
                  onClick={() => simulateNavigation(50)}
                  loading={loading === 'simulate-navigation'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                >
                  50 Sessions
                </Button>
                <Button
                  onClick={() => simulateNavigation(200)}
                  loading={loading === 'simulate-navigation'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                  variant="primary"
                >
                  🚀 200 Sessions (Recommended)
                </Button>
                <Button
                  onClick={() => simulateNavigation(500)}
                  loading={loading === 'simulate-navigation'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                >
                  500 Sessions
                </Button>
              </InlineStack>

              <Text as="p" fontWeight="semibold">🛒 Generate Realistic Sales (FBT Training):</Text>
              <Text as="p" tone="subdued">
                Creates realistic purchase patterns with product associations for training the
                Frequently Bought Together algorithm. Uses popularity distribution and customer segmentation.
              </Text>
              <InlineStack gap="300">
                <Button
                  onClick={() => generateRealisticSales(50, 30)}
                  loading={loading === 'generate-sales'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                >
                  50 Sales (1 month)
                </Button>
                <Button
                  onClick={() => generateRealisticSales(200, 90)}
                  loading={loading === 'generate-sales'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                  variant="primary"
                >
                  💰 200 Sales (3 months)
                </Button>
                <Button
                  onClick={() => generateRealisticSales(500, 180)}
                  loading={loading === 'generate-sales'}
                  disabled={!status?.shopExists || status?.productCount === 0}
                >
                  500 Sales (6 months)
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 4: Train ML Model */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 4: Train ML Model</Text>
              <Text as="p" tone="subdued">
                Train the Matrix Factorization model using the behavior data. 
                Requires at least 10 behaviors. This is the core AI from the development plan.
              </Text>
              <Button
                onClick={trainModel}
                loading={loading === 'train-model'}
                disabled={!status?.shopExists || (status?.behaviorCount ?? 0) < 10}
                variant="primary"
              >
                🤖 Train ML Model
              </Button>
              {status && status.mlModelsCount > 0 && (
                <Banner tone="success">
                  <p>{status.mlModelsCount} trained model(s) available</p>
                </Banner>
              )}
              {status && status.behaviorCount > 0 && status.behaviorCount < 10 && (
                <Banner tone="warning">
                  <p>Need at least 10 behaviors to train. Current: {status.behaviorCount}</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 4.5: Pre-Compute Recommendations */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 4.5: Pre-Compute Recommendations</Text>
              <Text as="p" tone="subdued">
                Force pre-calculation of recommendations. This runs automatically but can be triggered manually.
                Required for Similar, Trending, and Personalized recommendations to work properly.
              </Text>
              
              <InlineStack gap="200">
                <Button
                  onClick={preComputeAll}
                  loading={loading === 'precompute-all'}
                  disabled={!status?.shopExists}
                  variant="primary"
                >
                  🔄 Pre-Compute All (Recommended)
                </Button>
                <Button
                  onClick={preComputeStats}
                  loading={loading === 'precompute-stats'}
                  disabled={!status?.shopExists}
                >
                  📊 View Cache Stats
                </Button>
              </InlineStack>

              <InlineStack gap="200" wrap>
                <Button
                  onClick={() => preComputeTask('similar')}
                  loading={loading === 'precompute-similar'}
                  disabled={!status?.shopExists}
                  size="slim"
                >
                  Similar Products
                </Button>
                <Button
                  onClick={() => preComputeTask('trending-fbt')}
                  loading={loading === 'precompute-trending-fbt'}
                  disabled={!status?.shopExists}
                  size="slim"
                >
                  Trending + FBT
                </Button>
                <Button
                  onClick={() => preComputeTask('embeddings')}
                  loading={loading === 'precompute-embeddings'}
                  disabled={!status?.shopExists}
                  size="slim"
                >
                  Embeddings
                </Button>
                <Button
                  onClick={() => preComputeTask('ml-recommendations')}
                  loading={loading === 'precompute-ml-recommendations'}
                  disabled={!status?.shopExists || status?.mlModelsCount === 0}
                  size="slim"
                >
                  ML Recommendations
                </Button>
                <Button
                  onClick={clearRecommendationCache}
                  loading={loading === 'clear-cache'}
                  disabled={!status?.shopExists}
                  size="slim"
                  tone="critical"
                >
                  Clear Cache
                </Button>
              </InlineStack>

              {preComputeStatsData && (
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="200">
                    <Text as="h4" fontWeight="semibold">📈 Cache Statistics</Text>
                    <InlineStack gap="400" wrap>
                      <Box>
                        <Text as="span" tone="subdued">Similar: </Text>
                        <Text as="span" fontWeight="bold">{preComputeStatsData.validRecommendations?.similar || 0}</Text>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">Trending: </Text>
                        <Text as="span" fontWeight="bold">{preComputeStatsData.validRecommendations?.trending || 0}</Text>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">FBT: </Text>
                        <Text as="span" fontWeight="bold">{preComputeStatsData.validRecommendations?.frequentlyBoughtTogether || 0}</Text>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">Embeddings: </Text>
                        <Text as="span" fontWeight="bold">{preComputeStatsData.productEmbeddings || 0}</Text>
                      </Box>
                      <Box>
                        <Text as="span" tone="subdued">User Profiles: </Text>
                        <Text as="span" fontWeight="bold">{preComputeStatsData.userProfiles || 0}</Text>
                      </Box>
                    </InlineStack>
                    {preComputeStatsData.lastGenerated?.similar && (
                      <Text as="p" tone="subdued" variant="bodySm">
                        Last Similar: {new Date(preComputeStatsData.lastGenerated.similar).toLocaleString()}
                      </Text>
                    )}
                  </BlockStack>
                </Box>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 5: Test Recommendations */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 5: Test Recommendations</Text>
              <Text as="p" tone="subdued">
                Verify that the recommendation engine is working correctly.
              </Text>
              <Button
                onClick={testRecommendations}
                loading={loading === 'recommendations'}
                disabled={!status?.shopExists || status?.behaviorCount === 0}
                variant="primary"
              >
                🎯 Test Recommendations
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 6: Install Widget on Store */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Step 6: Install Widget on Store</Text>
              <Text as="p" tone="subdued">
                Install the Nexus AI recommendations widget on your store. This automatically shows 
                personalized product recommendations on every product page. No code editing required!
              </Text>
              
              <ButtonGroup>
                <Button
                  onClick={installWidget}
                  loading={loading === 'install-widget'}
                  disabled={!status?.shopExists}
                  variant="primary"
                >
                  🚀 Install Widget on Store
                </Button>
                <Button
                  onClick={uninstallWidget}
                  loading={loading === 'uninstall-widget'}
                  disabled={!status?.shopExists}
                  tone="critical"
                >
                  Uninstall Widget
                </Button>
              </ButtonGroup>

              {status?.shopExists && (
                <Banner tone="info">
                  <p>
                    <strong>After installing:</strong> Visit any product page on your store to see AI-powered recommendations.
                    {' '}
                    <a href={`https://${status.shopDomain}/products`} target="_blank" rel="noopener noreferrer">
                      Open Store Products →
                    </a>
                  </p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 7: A/B Testing */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">🧪 Step 7: A/B Testing</Text>
                <Badge tone="info">ML Phase</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Create experiments, simulate traffic, and verify significance testing.
              </Text>
              {abExperimentId && (
                <Banner tone="info">
                  <p>Active Experiment ID: <strong>{abExperimentId}</strong></p>
                </Banner>
              )}
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('ab/create-sample'), 'POST').then(r => {
                  const d = r.data || r;
                  const expId = d?.experimentId;
                  if (expId) setAbExperimentId(expId);
                  setAbResult(d);
                  addLog(`A/B: ${r.message || 'Experiment created'} — ID: ${expId || 'N/A'}`);
                  setLastResult(r);
                })} loading={loading === 'ab-create'} disabled={!status?.shopExists}>
                  Create Sample Experiment
                </Button>
                <Button onClick={() => {
                  const expId = abExperimentId || prompt('Enter experiment ID:');
                  if (expId) apiCall(withShop(`ab/simulate?experimentId=${expId}&sessions=200`), 'POST').then(r => {
                    const d = r.data || r;
                    setAbResult(d);
                    addLog(`A/B: Simulated ${d?.sessions || 0} sessions`);
                    setLastResult(r);
                  });
                }} disabled={!status?.shopExists}>
                  Simulate 200 Sessions
                </Button>
                <Button onClick={() => {
                  const expId = abExperimentId || prompt('Enter experiment ID:');
                  if (expId) apiCall(withShop(`ab/results?experimentId=${expId}`)).then(r => {
                    const d = r.data || r;
                    setAbResult(d);
                    addLog('A/B: Results loaded');
                    setLastResult(r);
                  });
                }} disabled={!status?.shopExists}>
                  View Results
                </Button>
              </ButtonGroup>
              {abResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Last Response</Text>
                    {/* Experiment created */}
                    {(abResult as any)?.experimentId && !(abResult as any)?.results && (
                      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="200">
                          <InlineStack gap="400">
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Experiment ID</Text>
                              <Text as="p" variant="bodyMd" fontWeight="bold">{(abResult as any).experimentId}</Text>
                            </BlockStack>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Name</Text>
                              <Text as="p" variant="bodyMd">{(abResult as any).name || 'N/A'}</Text>
                            </BlockStack>
                          </InlineStack>
                          {(abResult as any)?.variants && (
                            <DataTable
                              columnContentTypes={['text', 'text', 'numeric']}
                              headings={['Variant', 'Algorithm', 'Traffic %']}
                              rows={((abResult as any).variants || []).map((v: any) => [
                                v.variantId || v.VariantId,
                                v.algorithm || v.Algorithm || 'N/A',
                                `${v.trafficPercent || v.TrafficPercent || 0}%`,
                              ])}
                            />
                          )}
                        </BlockStack>
                      </Box>
                    )}
                    {/* Simulation result */}
                    {(abResult as any)?.sessions && !(abResult as any)?.results && !(abResult as any)?.experimentId && (
                      <InlineStack gap="400" wrap>
                        {[
                          { label: 'Sessions', value: (abResult as any).sessions },
                          { label: 'Impressions', value: (abResult as any).impressions },
                          { label: 'Clicks', value: (abResult as any).clicks },
                          { label: 'Conversions', value: (abResult as any).conversions },
                        ].map(({ label, value }) => (
                          <Card key={label}>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">{label}</Text>
                              <Text as="p" variant="headingLg">{(value ?? 0).toLocaleString()}</Text>
                            </BlockStack>
                          </Card>
                        ))}
                      </InlineStack>
                    )}
                    {/* Results with significance */}
                    {(abResult as any)?.results && (
                      <BlockStack gap="300">
                        <Banner tone={(abResult as any).results.isSignificant ? 'success' : 'info'}>
                          <p>
                            {(abResult as any).results.isSignificant
                              ? `✅ Statistically significant (p-value: ${((abResult as any).results.pValue ?? 0).toFixed(4)})`
                              : `⏳ Not yet significant (p-value: ${((abResult as any).results.pValue ?? 1).toFixed(4)}). More data needed.`}
                          </p>
                        </Banner>
                        {(abResult as any).results.variantResults && (
                          <DataTable
                            columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
                            headings={['Variant', 'Type', 'Impressions', 'Conversions', 'CVR']}
                            rows={((abResult as any).results.variantResults || []).map((v: any) => [
                              v.variantName || v.VariantName || 'Unknown',
                              v.isControl ? 'Control' : 'Treatment',
                              (v.impressions ?? 0).toLocaleString(),
                              (v.conversions ?? 0).toLocaleString(),
                              `${((v.conversionRate ?? 0) * 100).toFixed(2)}%`,
                            ])}
                          />
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 8: Attribution Testing */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">📊 Step 8: Attribution Tracking</Text>
                <Badge tone="info">Revenue Pipeline</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Simulate the full attribution funnel: impressions → clicks → add-to-cart → conversions.
              </Text>
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('attribution/simulate?sessions=50'), 'POST').then(r => {
                  const d = r.data || r;
                  setAttributionResult(d);
                  addLog(`Attribution: ${d?.clicks || 0} clicks, ${d?.conversions || 0} conversions`);
                  setLastResult(r);
                })} loading={loading === 'attr-sim-50'} disabled={!status?.shopExists}>
                  Simulate 50 Sessions
                </Button>
                <Button onClick={() => apiCall(withShop('attribution/simulate?sessions=200'), 'POST').then(r => {
                  const d = r.data || r;
                  setAttributionResult(d);
                  addLog(`Attribution: ${d?.clicks || 0} clicks, ${d?.conversions || 0} conversions`);
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  Simulate 200 Sessions
                </Button>
                <Button onClick={() => apiCall(withShop('attribution/status')).then(r => {
                  const d = r.data || r;
                  setAttributionResult(d);
                  addLog('Attribution: Status loaded');
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  View Attribution Status
                </Button>
              </ButtonGroup>
              {attributionResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Attribution Funnel</Text>
                    {/* Simulation results */}
                    {(attributionResult as any)?.impressions != null && (
                      <BlockStack gap="200">
                        <InlineStack gap="400" wrap>
                          {[
                            { label: 'Impressions', value: (attributionResult as any).impressions, color: 'bg-surface-secondary' },
                            { label: 'Clicks', value: (attributionResult as any).clicks, color: 'bg-surface-secondary' },
                            { label: 'Add to Cart', value: (attributionResult as any).addToCarts, color: 'bg-surface-secondary' },
                            { label: 'Conversions', value: (attributionResult as any).conversions, color: 'bg-surface-secondary' },
                          ].map(({ label, value }) => (
                            <Card key={label}>
                              <BlockStack gap="100">
                                <Text as="p" tone="subdued" variant="bodySm">{label}</Text>
                                <Text as="p" variant="headingLg">{(value ?? 0).toLocaleString()}</Text>
                              </BlockStack>
                            </Card>
                          ))}
                        </InlineStack>
                        {/* Funnel conversion bars */}
                        {(() => {
                          const imp = (attributionResult as any)?.impressions || 1;
                          const clicks = (attributionResult as any)?.clicks || 0;
                          const carts = (attributionResult as any)?.addToCarts || 0;
                          const conv = (attributionResult as any)?.conversions || 0;
                          return (
                            <BlockStack gap="100">
                              <InlineStack align="space-between"><Text as="p" variant="bodySm">CTR (Click/Impression)</Text><Text as="p" variant="bodySm">{((clicks / imp) * 100).toFixed(1)}%</Text></InlineStack>
                              <ProgressBar progress={Math.min((clicks / imp) * 100, 100)} tone="primary" size="small" />
                              <InlineStack align="space-between"><Text as="p" variant="bodySm">ATC Rate (Cart/Click)</Text><Text as="p" variant="bodySm">{clicks > 0 ? ((carts / clicks) * 100).toFixed(1) : '0.0'}%</Text></InlineStack>
                              <ProgressBar progress={clicks > 0 ? Math.min((carts / clicks) * 100, 100) : 0} tone="success" size="small" />
                              <InlineStack align="space-between"><Text as="p" variant="bodySm">Conversion Rate (Conv/Impression)</Text><Text as="p" variant="bodySm">{((conv / imp) * 100).toFixed(1)}%</Text></InlineStack>
                              <ProgressBar progress={Math.min((conv / imp) * 100, 100)} tone="highlight" size="small" />
                            </BlockStack>
                          );
                        })()}
                      </BlockStack>
                    )}
                    {/* Overview status response */}
                    {(attributionResult as any)?.overview && (
                      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>
                          {JSON.stringify((attributionResult as any).overview, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 9: Bandit (Thompson Sampling) */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">🎰 Step 9: Thompson Sampling Bandit</Text>
                <Badge tone="info">Auto-Optimization</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Seed bandit arms and verify the explore/exploit algorithm is working.
              </Text>
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('bandit/seed'), 'POST').then(r => {
                  const d = r.data || r;
                  setBanditResult(d);
                  addLog(`Bandit: ${d?.armsSeeded || 0} arms seeded`);
                  setLastResult(r);
                })} loading={loading === 'bandit-seed'} disabled={!status?.shopExists}>
                  Seed Bandit Arms
                </Button>
                <Button onClick={() => apiCall(withShop('bandit/status')).then(r => {
                  const d = r.data || r;
                  setBanditResult(d);
                  addLog(`Bandit: ${d?.totalArms || 0} total arms`);
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  View Bandit Status
                </Button>
              </ButtonGroup>
              {banditResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Bandit Status</Text>
                    {/* Seed result */}
                    {(banditResult as any)?.armsSeeded != null && !(banditResult as any)?.byRecommendationType && (
                      <Banner tone="success">
                        <p>✅ Seeded <strong>{(banditResult as any).armsSeeded}</strong> bandit arms. {(banditResult as any).message}</p>
                      </Banner>
                    )}
                    {/* Status with breakdown by rec type */}
                    {(banditResult as any)?.totalArms != null && (
                      <BlockStack gap="200">
                        <InlineStack gap="400" wrap>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Total Arms</Text>
                              <Text as="p" variant="headingLg">{(banditResult as any).totalArms}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Source Products</Text>
                              <Text as="p" variant="headingLg">{(banditResult as any).uniqueSourceProducts ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          {(banditResult as any)?.lastUpdate && (
                            <Card>
                              <BlockStack gap="100">
                                <Text as="p" tone="subdued" variant="bodySm">Last Updated</Text>
                                <Text as="p" variant="bodyMd">{new Date((banditResult as any).lastUpdate).toLocaleString()}</Text>
                              </BlockStack>
                            </Card>
                          )}
                        </InlineStack>
                        {(banditResult as any)?.byRecommendationType && (
                          <DataTable
                            columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                            headings={['Rec Type', 'Arms', 'Impressions', 'Clicks', 'Conversions', 'Avg α', 'Avg β']}
                            rows={((banditResult as any).byRecommendationType || []).map((g: any) => [
                              g.recType || 'Unknown',
                              (g.armCount ?? 0).toString(),
                              (g.totalImpressions ?? 0).toLocaleString(),
                              (g.totalClicks ?? 0).toLocaleString(),
                              (g.totalConversions ?? 0).toLocaleString(),
                              (g.avgAlpha ?? 0).toFixed(1),
                              (g.avgBeta ?? 0).toFixed(1),
                            ])}
                          />
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 10: Semantic Embeddings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">🧠 Step 10: Semantic Embeddings</Text>
                <Badge tone="info">OpenAI</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Generate OpenAI text embeddings for semantic product similarity (requires API key).
              </Text>
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('semantic/generate?count=10'), 'POST').then(r => {
                  const d = r.data || r;
                  setSemanticResult(d);
                  addLog(`Semantic: ${d?.saved || 0} embeddings generated`);
                  setLastResult(r);
                })} loading={loading === 'semantic-gen'} disabled={!status?.shopExists}>
                  Generate 10 Embeddings
                </Button>
                <Button onClick={() => apiCall(withShop('semantic/generate?count=50'), 'POST').then(r => {
                  const d = r.data || r;
                  setSemanticResult(d);
                  addLog(`Semantic: ${d?.saved || 0} embeddings generated`);
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  Generate 50 Embeddings
                </Button>
                <Button onClick={() => apiCall(withShop('semantic/status')).then(r => {
                  const d = r.data || r;
                  setSemanticResult(d);
                  addLog(`Semantic: ${d?.coverage || 0}% coverage`);
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  View Embedding Status
                </Button>
              </ButtonGroup>
              {semanticResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Embedding Status</Text>
                    {/* Generation result */}
                    {(semanticResult as any)?.saved != null && (semanticResult as any)?.coverage == null && (
                      <Banner tone="success">
                        <p>Generated <strong>{(semanticResult as any).generated ?? 0}</strong> embeddings, saved <strong>{(semanticResult as any).saved}</strong> ({(semanticResult as any).dimensions ?? 0} dimensions each).</p>
                      </Banner>
                    )}
                    {/* Coverage status */}
                    {(semanticResult as any)?.coverage != null && (
                      <BlockStack gap="200">
                        <InlineStack gap="400" wrap>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Total Products</Text>
                              <Text as="p" variant="headingLg">{(semanticResult as any).totalProducts ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">With Embeddings</Text>
                              <Text as="p" variant="headingLg">{(semanticResult as any).withEmbeddings ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">With Similar Products</Text>
                              <Text as="p" variant="headingLg">{(semanticResult as any).withSimilarProducts ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Dimensions</Text>
                              <Text as="p" variant="headingLg">{(semanticResult as any).dimensions ?? 0}</Text>
                            </BlockStack>
                          </Card>
                        </InlineStack>
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text as="p" variant="bodySm" fontWeight="semibold">Coverage</Text>
                            <Text as="p" variant="bodySm">{(semanticResult as any).coverage}%</Text>
                          </InlineStack>
                          <ProgressBar progress={(semanticResult as any).coverage ?? 0} tone="primary" size="small" />
                        </BlockStack>
                      </BlockStack>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 11: Session Scoring & Popups */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">🎯 Step 11: Session Scoring & Popups</Text>
                <Badge tone="info">Intent Engine</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Enable real-time purchase intent scoring with exit-intent popups and dynamic product counts.
              </Text>
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('session-scoring/enable'), 'POST').then(r => {
                  const d = r.data || r;
                  setSessionScoringResult(d);
                  addLog('Session Scoring: Enabled with defaults');
                  setLastResult(r);
                })} loading={loading === 'scoring-enable'} disabled={!status?.shopExists}>
                  Enable Session Scoring
                </Button>
                <Button onClick={() => apiCall(withShop('session-scoring/status')).then(r => {
                  const d = r.data || r;
                  setSessionScoringResult(d);
                  addLog('Session Scoring: Status loaded');
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  View Status
                </Button>
              </ButtonGroup>
              {sessionScoringResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Session Scoring Configuration</Text>
                    {(sessionScoringResult as any)?.message && !(sessionScoringResult as any)?.sessionScoring && (
                      <Banner tone="success">
                        <p>{(sessionScoringResult as any).message}</p>
                      </Banner>
                    )}
                    {(sessionScoringResult as any)?.sessionScoring && (
                      <BlockStack gap="200">
                        <Banner tone={(sessionScoringResult as any).sessionScoring.enabled ? 'success' : 'warning'}>
                          <p>Session scoring is <strong>{(sessionScoringResult as any).sessionScoring.enabled ? 'ACTIVE' : 'DISABLED'}</strong></p>
                        </Banner>
                        {(sessionScoringResult as any).sessionScoring.weights && (
                          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                            <BlockStack gap="100">
                              <Text as="p" fontWeight="semibold" variant="bodySm">Scoring Weights</Text>
                              {Object.entries((sessionScoringResult as any).sessionScoring.weights || {}).map(([key, val]) => (
                                <InlineStack key={key} align="space-between">
                                  <Text as="p" variant="bodySm">{key}</Text>
                                  <Text as="p" variant="bodySm" fontWeight="bold">{String(val)}</Text>
                                </InlineStack>
                              ))}
                            </BlockStack>
                          </Box>
                        )}
                        {(sessionScoringResult as any).sessionScoring.thresholds && (
                          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                            <BlockStack gap="100">
                              <Text as="p" fontWeight="semibold" variant="bodySm">Intent Thresholds</Text>
                              {Object.entries((sessionScoringResult as any).sessionScoring.thresholds || {}).map(([key, val]) => (
                                <InlineStack key={key} align="space-between">
                                  <Text as="p" variant="bodySm">{key}</Text>
                                  <Text as="p" variant="bodySm" fontWeight="bold">{String(val)}</Text>
                                </InlineStack>
                              ))}
                            </BlockStack>
                          </Box>
                        )}
                      </BlockStack>
                    )}
                    {(sessionScoringResult as any)?.popupSettings && (
                      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="100">
                          <Text as="p" fontWeight="semibold" variant="bodySm">Popup Settings</Text>
                          <InlineStack align="space-between"><Text as="p" variant="bodySm">Enabled</Text><Badge tone={(sessionScoringResult as any).popupSettings.enabled ? 'success' : 'critical'}>{(sessionScoringResult as any).popupSettings.enabled ? 'Yes' : 'No'}</Badge></InlineStack>
                          <InlineStack align="space-between"><Text as="p" variant="bodySm">Exit Intent</Text><Badge tone={(sessionScoringResult as any).popupSettings.exitIntentEnabled ? 'success' : 'critical'}>{(sessionScoringResult as any).popupSettings.exitIntentEnabled ? 'Yes' : 'No'}</Badge></InlineStack>
                          <InlineStack align="space-between"><Text as="p" variant="bodySm">High Intent Upsell</Text><Badge tone={(sessionScoringResult as any).popupSettings.highIntentUpsellEnabled ? 'success' : 'critical'}>{(sessionScoringResult as any).popupSettings.highIntentUpsellEnabled ? 'Yes' : 'No'}</Badge></InlineStack>
                          {(sessionScoringResult as any).popupSettings.aggressiveness && (
                            <>
                              <Divider />
                              <Text as="p" fontWeight="semibold" variant="bodySm">Aggressiveness</Text>
                              {Object.entries((sessionScoringResult as any).popupSettings.aggressiveness || {}).map(([key, val]) => (
                                <InlineStack key={key} align="space-between">
                                  <Text as="p" variant="bodySm">{key}</Text>
                                  <Text as="p" variant="bodySm" fontWeight="bold">{String(val)}</Text>
                                </InlineStack>
                              ))}
                            </>
                          )}
                        </BlockStack>
                      </Box>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Step 12: Abandoned Cart Emails */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">📧 Step 12: Abandoned Cart Emails</Text>
                <Badge tone="warning">Email</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">
                Test the abandoned cart recovery flow: detect abandoned carts and trigger personalized email campaigns.
              </Text>
              <ButtonGroup>
                <Button onClick={() => apiCall(withShop('create-test-abandoned-cart'), 'POST').then(r => {
                  const d = r.data || r;
                  setAbandonedCartResult(d);
                  addLog('Abandoned Cart: Test cart created');
                  setLastResult(r);
                })} loading={loading === 'create-cart'} disabled={!status?.shopExists}>
                  Create Test Cart
                </Button>
                <Button onClick={() => apiCall(withShop('process-abandoned-carts'), 'POST').then(r => {
                  const d = r.data || r;
                  setAbandonedCartResult(d);
                  addLog('Abandoned Cart: Processing triggered');
                  setLastResult(r);
                })} disabled={!status?.shopExists}>
                  Process Abandoned Carts
                </Button>
              </ButtonGroup>
              {abandonedCartResult && (
                <>
                  <Divider />
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Abandoned Cart Results</Text>
                    {/* Cart creation result */}
                    {(abandonedCartResult as any)?.cartCreated && (
                      <BlockStack gap="200">
                        <Banner tone="success">
                          <p>Test cart created for <strong>{(abandonedCartResult as any).email || 'N/A'}</strong></p>
                        </Banner>
                        <InlineStack gap="400" wrap>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Cart Items</Text>
                              <Text as="p" variant="headingLg">{(abandonedCartResult as any).cartItems ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Total Price</Text>
                              <Text as="p" variant="headingLg">${((abandonedCartResult as any).cartTotalPrice ?? 0).toFixed(2)}</Text>
                            </BlockStack>
                          </Card>
                        </InlineStack>
                      </BlockStack>
                    )}
                    {/* Process result */}
                    {((abandonedCartResult as any)?.synced != null || (abandonedCartResult as any)?.processResult) && (
                      <BlockStack gap="200">
                        <InlineStack gap="400" wrap>
                          {(abandonedCartResult as any)?.synced != null && (
                            <Card>
                              <BlockStack gap="100">
                                <Text as="p" tone="subdued" variant="bodySm">Synced</Text>
                                <Text as="p" variant="headingLg">{(abandonedCartResult as any).synced}</Text>
                              </BlockStack>
                            </Card>
                          )}
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Processed</Text>
                              <Text as="p" variant="headingLg">{(abandonedCartResult as any).processResult?.processed ?? (abandonedCartResult as any).processed ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Emails Sent</Text>
                              <Text as="p" variant="headingLg">{(abandonedCartResult as any).processResult?.sent ?? (abandonedCartResult as any).sent ?? 0}</Text>
                            </BlockStack>
                          </Card>
                          <Card>
                            <BlockStack gap="100">
                              <Text as="p" tone="subdued" variant="bodySm">Failed</Text>
                              <Text as="p" variant="headingLg">{(abandonedCartResult as any).processResult?.failed ?? (abandonedCartResult as any).failed ?? 0}</Text>
                            </BlockStack>
                          </Card>
                        </InlineStack>
                      </BlockStack>
                    )}
                    {/* Diagnostics / blockers */}
                    {(abandonedCartResult as any)?.diagnostics && (
                      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="100">
                          <Text as="p" fontWeight="semibold" variant="bodySm">Email Diagnostics</Text>
                          {Object.entries((abandonedCartResult as any).diagnostics || {}).map(([key, val]) => (
                            <InlineStack key={key} align="space-between">
                              <Text as="p" variant="bodySm">{key}</Text>
                              <Text as="p" variant="bodySm" fontWeight="bold">{typeof val === 'object' ? JSON.stringify(val) : String(val ?? 'N/A')}</Text>
                            </InlineStack>
                          ))}
                        </BlockStack>
                      </Box>
                    )}
                    {(abandonedCartResult as any)?.note && (
                      <Banner tone="warning">
                        <p><strong>Blockers:</strong> {(abandonedCartResult as any).note}</p>
                      </Banner>
                    )}
                    {(abandonedCartResult as any)?.processError && (
                      <Banner tone="critical">
                        <p><strong>Error:</strong> {(abandonedCartResult as any).processError}</p>
                      </Banner>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Reset Data */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">⚠️ Reset Data</Text>
              <Text as="p" tone="subdued">
                Clear test data from the database. Use with caution.
              </Text>
              <ButtonGroup>
                <Button
                  onClick={() => resetData(false)}
                  loading={loading === 'reset'}
                  tone="critical"
                >
                  Reset Products & Behaviors
                </Button>
                <Button
                  onClick={() => resetData(true)}
                  loading={loading === 'reset'}
                  tone="critical"
                >
                  Reset Everything (including Shop)
                </Button>
              </ButtonGroup>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Last Result */}
        {lastResult && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  disclosure={showDetails ? 'up' : 'down'}
                  variant="plain"
                >
                  Last API Response
                </Button>
                <Collapsible open={showDetails} id="details">
                  <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>
                      {JSON.stringify(lastResult, null, 2)}
                    </pre>
                  </Box>
                </Collapsible>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Activity Log */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">Activity Log</Text>
                <Button onClick={() => setLogs([])} variant="plain" tone="critical">
                  Clear
                </Button>
              </InlineStack>
              <Box 
                padding="300" 
                background="bg-surface-secondary" 
                borderRadius="200"
                minHeight="150px"
                maxWidth="100%"
              >
                {logs.length === 0 ? (
                  <Text as="p" tone="subdued">No activity yet. Start by refreshing status.</Text>
                ) : (
                  <List>
                    {logs.map((log, i) => (
                      <List.Item key={i}>
                        <Text as="span" variant="bodySm">{log}</Text>
                      </List.Item>
                    ))}
                  </List>
                )}
              </Box>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
